import { inject, injectable } from '@wix/typesafe-inversify';
import type { BlocksAppDescriptor } from '@wix/document-services-types';
import jsonSchemaUtils from '@wix/wix-json-schema-utils';
import type { IFedopsLogger } from '../FedopsLogger/FedopsLogger';
import {
  appAddsPages,
  getEditorPresenceByWidgetId,
  getInstallationSettingsByWidgetId,
  getPagesManifest,
} from '../../utils/installationSettingsUtils';
import type { ITranslations } from '../Translations/ITranslations';
import type { ExtendedBlocksAppData } from '../../types/ExtendedBlocksAppData';
import type { IManifestBuilder } from './IManifestBuilder';
import { Services } from '../../ioc/services';
import type { IBobWidgetManifestFetcher } from '../BobWidgetServices/IBobWidgetManifestFetcher';
import type { IBobWidgetImages } from '../BobWidgetServices/IBobWidgetImages';
import {
  createAddPanelSectionDefinition,
  createControllersStageData,
  createExports,
  createWidgets,
  getPresetsToShowInAddPanel,
} from '../../utils/manifestUtils';
import { removeHash } from '../../utils/removeHash';
import type { IExperiments } from '../Experiments/IExperiments';
import { ExperimentNames } from '../Experiments/ExperimentNames';

@injectable()
export class ManifestBuilder implements IManifestBuilder {
  constructor(
    @inject(Services.BobWidgetManifestFetcher)
    private bobWidgetManifestFetcher: IBobWidgetManifestFetcher,
    @inject(Services.BobWidgetImages)
    private bobWidgetImages: IBobWidgetImages,
    @inject(Services.FedopsLogger)
    private fedopsLogger: IFedopsLogger,
    @inject(Services.Translations)
    private translations: ITranslations,
    @inject(Services.Experiments)
    private experiments: IExperiments,
  ) {}

  async generateAppManifest(
    appDescriptor: BlocksAppDescriptor | undefined,
    appData: ExtendedBlocksAppData,
    isResponsive: boolean,
  ) {
    const widgetEntries = appDescriptor?.widgets
      ? Object.entries(appDescriptor.widgets)
      : [];

    const settingsById = getInstallationSettingsByWidgetId(appData);
    const editorPresenceById = getEditorPresenceByWidgetId(appData);

    const widgetsEntriesAndPresets = widgetEntries
      .filter(([widgetId]) => {
        if (
          editorPresenceById[widgetId] &&
          editorPresenceById[widgetId]?.presetsEditorPresence
        ) {
          return editorPresenceById[widgetId]?.presetsEditorPresence?.some(
            (p) => p.showInAddPanel,
          );
        }
        return settingsById[widgetId]?.showInAddPanel ?? true;
      })
      .map(([widgetId, widgetDescriptor]) => {
        return {
          widgetId,
          widgetDescriptor,
          presetsToShowInAddPanel: getPresetsToShowInAddPanel({
            firstPresetId: removeHash(widgetDescriptor.presets?.[0]?.presetId),
            editorPresence: editorPresenceById[widgetId],
            installationSettings: settingsById[widgetId],
          }),
        };
      });

    const shouldIncludePagesManifest = appAddsPages(appData);

    return {
      exports: createExports({
        appData,
        schemaDriver: jsonSchemaUtils.createDriver(
          appDescriptor?.customDefinitions,
        ),
        widgetEntries,
      }),
      widgets: createWidgets(widgetEntries),
      controllersStageData: await createControllersStageData({
        appData,
        fedopsLogger: this.fedopsLogger,
        bobWidgetManifestFetcher: this.bobWidgetManifestFetcher,
        bobWidgetImages: this.bobWidgetImages,
        translations: this.translations,
        widgetEntries,
      }),
      customDefinitions: appDescriptor?.customDefinitions,
      addPanelSectionDefinition: createAddPanelSectionDefinition({
        appData,
        widgetsEntriesAndPresets,
      }),
      ...(shouldIncludePagesManifest
        ? getPagesManifest(
            isResponsive,
            this.translations,
            this.experiments.enabled(ExperimentNames.FixPagesMenuTabs),
          )
        : {}),
    };
  }
}
